<script setup lang="ts">
import type { BTableSortBy } from 'bootstrap-vue-next';
import { BPagination } from 'bootstrap-vue-next';
import { onMounted, ref } from 'vue';

import type { GetApiV1UsersByUsernameMenteesResponse } from '~/api/generated';
import { fetchMentees } from '~/api/users';
import TableWithBusyState from '~/common/components/TableWithBusyState.vue';
import UserWithGitLabProfileLink from '~/common/components/UserWithGitLabProfileLink.vue';
import { dateFieldFormatter } from '~/common/FieldFormats';

const props = defineProps<{
  userHandle: string;
}>();

const emit = defineEmits<{ 'count-updated': [count: number] }>();

const mentees = ref<GetApiV1UsersByUsernameMenteesResponse>([]);
const loading = ref(false);
const currentPage = ref(1);
const perPage = ref(10);

const sortBy = ref<BTableSortBy[]>([{ key: 'username', order: 'asc' }]);

const menteeFields = [
  {
    key: 'username',
    label: 'Username',
    sortable: true,
  },
  {
    key: 'start_date',
    label: 'Start date',
    sortable: true,
    formatter: dateFieldFormatter,
  },
];

const loadMentees = async () => {
  loading.value = true;
  try {
    mentees.value = await fetchMentees(props.userHandle);
    emit('count-updated', mentees.value.length);
  } catch {
    mentees.value = [];
    emit('count-updated', 0);
  } finally {
    loading.value = false;
  }
};

onMounted(loadMentees);
</script>

<template>
  <div>
    <TableWithBusyState
      v-model:sort-by="sortBy"
      :fields="menteeFields"
      :items="mentees"
      :busy="loading"
      :per-page="perPage"
      :current-page="currentPage"
      show-empty
    >
      <template #cell(username)="{ value }">
        <UserWithGitLabProfileLink
          :username="value"
        />
      </template>
    </TableWithBusyState>

    <BPagination
      v-if="mentees.length > perPage"
      v-model="currentPage"
      :total-rows="mentees.length"
      :per-page="perPage"
      align="center"
      first-number
      last-number
      class="mt-3 mb-0"
    />
  </div>
</template>
